import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  createDefaultPerClassificationQuestion,
  createDefaultPerPostQuestion,
  type AiVerificationQuestion,
  type AiVerificationTemplate,
  type DistrictMode,
  type VerificationTemplatePreset,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DefaultDistributionSection,
  QuestionListEditor,
  type DefaultDistributionValue,
} from '../verification-admin';
import { upsertMaster, useVerificationTemplatePresets } from './useMasters';
import { ModeSelectField } from './components/ModeSelectField';

/**
 * M9 verificationTemplatePresets 個別 page (visual editor、Sprint BG-DataSettings 13 / A-S24-S25)。
 *
 * 旧 v1 `old/apps/admin/src/pages/MasterVerificationTemplatesPage.tsx` +
 * `VerificationTemplateEditorPage.tsx` の visual editor を v2 で踏襲。
 * 地区 VerificationTemplateEditPage と類似構造で、preset 専用に minimum field を持つ:
 *   - displayName: master 一覧用の人間可読名
 *   - mode: master の DistrictMode (M9 MasterCommon の外側 field)
 *   - template (preset 本体): { displayName, description, targetMode, perPostQuestions,
 *     perClassificationQuestions, allowFreeComment, status, defaults }
 *
 * 既存 SimpleJsonMasterPage の JSON モード は VerificationTemplatePresetsPage 側で温存
 * (本 page は併用、JSON モードで作った doc も visual editor で読み込める後方互換)。
 *
 * 認可: master role のみ (rules で deny されるが UI でも guard、admin-stub uid のまま minimum)。
 */

type TemplateStatus = AiVerificationTemplate['status'];

const STATUS_LABEL: Record<TemplateStatus, string> = {
  draft: '下書き',
  published: '配信中',
  archived: 'アーカイブ',
};

/**
 * targetMode の保存値変換 (MD-3 モードドリブン化 2026-07-24)。
 * 旧実装は組込 4 種を whitelist し custom mode を null に落としていた (バグ) →
 * mode キーは ModeProfile 駆動の任意 string になったため、非空ならそのまま保存する。
 */
function asTargetMode(v: string): DistrictMode | null {
  const trimmed = v.trim();
  return trimmed ? trimmed : null;
}

function asTemplateStatus(v: unknown): TemplateStatus {
  return v === 'published' || v === 'archived' ? v : 'draft';
}

/** preset.template (unknown) を visual editor の初期 state に落とし込む安全 narrow。 */
function readBody(preset: VerificationTemplatePreset): {
  templateDisplayName: string;
  description: string;
  targetMode: string;
  status: TemplateStatus;
  allowFreeComment: boolean;
  perPostQuestions: AiVerificationQuestion[];
  perClassificationQuestions: AiVerificationQuestion[];
  distribution: DefaultDistributionValue;
} {
  const t = preset.template;
  if (!t || typeof t !== 'object' || Array.isArray(t)) {
    return {
      templateDisplayName: '',
      description: '',
      targetMode: '',
      status: 'draft',
      allowFreeComment: true,
      perPostQuestions: [],
      perClassificationQuestions: [],
      distribution: {},
    };
  }
  const body = t as Record<string, unknown>;
  const perPost = Array.isArray(body.perPostQuestions)
    ? body.perPostQuestions.filter(
        (q): q is AiVerificationQuestion =>
          typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
      )
    : [];
  const perCls = Array.isArray(body.perClassificationQuestions)
    ? body.perClassificationQuestions.filter(
        (q): q is AiVerificationQuestion =>
          typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
      )
    : [];
  // AW4 #8 — 配分デフォルトを専用フォーム用 value に narrow
  const distribution: DefaultDistributionValue = {};
  if (
    body.defaultDistribution === 'free' ||
    body.defaultDistribution === 'all_same' ||
    body.defaultDistribution === 'random_per_user' ||
    body.defaultDistribution === 'manual'
  ) {
    distribution.defaultDistribution = body.defaultDistribution;
  }
  if (body.defaultRandomConstraints && typeof body.defaultRandomConstraints === 'object') {
    distribution.defaultRandomConstraints =
      body.defaultRandomConstraints as DefaultDistributionValue['defaultRandomConstraints'];
  }
  if (body.defaultCandidateCriteria && typeof body.defaultCandidateCriteria === 'object') {
    distribution.defaultCandidateCriteria =
      body.defaultCandidateCriteria as DefaultDistributionValue['defaultCandidateCriteria'];
  }
  if (typeof body.defaultEndAtOffsetDays === 'number') {
    distribution.defaultEndAtOffsetDays = body.defaultEndAtOffsetDays;
  }
  return {
    templateDisplayName: typeof body.displayName === 'string' ? body.displayName : '',
    description: typeof body.description === 'string' ? body.description : '',
    targetMode:
      body.targetMode === 'ecw' ||
      body.targetMode === 'bousai' ||
      body.targetMode === 'walker' ||
      body.targetMode === 'kids-bousai'
        ? body.targetMode
        : '',
    status: asTemplateStatus(body.status),
    allowFreeComment: typeof body.allowFreeComment === 'boolean' ? body.allowFreeComment : true,
    perPostQuestions: perPost,
    perClassificationQuestions: perCls,
    distribution,
  };
}

export function VerificationTemplatePresetEditPage() {
  const { presetId } = useParams<{ presetId: string }>();
  const presetsState = useVerificationTemplatePresets();
  const { toast } = useToast();

  const target = useMemo(() => {
    if (presetsState.status !== 'ready' || !presetId) return null;
    return presetsState.items.find((p) => p.id === presetId) ?? null;
  }, [presetsState, presetId]);

  // master-level fields
  const [displayName, setDisplayName] = useState('');
  const [mode, setMode] = useState<DistrictMode>('ecw');
  // template body fields (preset.template の内側)
  const [templateDisplayName, setTemplateDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [targetMode, setTargetMode] = useState<string>('');
  const [status, setStatus] = useState<TemplateStatus>('draft');
  const [allowFreeComment, setAllowFreeComment] = useState(true);
  const [perPostQuestions, setPerPostQuestions] = useState<AiVerificationQuestion[]>([]);
  const [perClsQuestions, setPerClsQuestions] = useState<AiVerificationQuestion[]>([]);
  // AW4 #8 — 配分デフォルトを生 JSON から専用フォーム value に置換
  const [distribution, setDistribution] = useState<DefaultDistributionValue>({});
  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!target || initialized) return;
    setDisplayName(target.displayName);
    setMode(target.mode);
    const body = readBody(target);
    setTemplateDisplayName(body.templateDisplayName);
    setDescription(body.description);
    setTargetMode(body.targetMode);
    setStatus(body.status);
    setAllowFreeComment(body.allowFreeComment);
    setPerPostQuestions(body.perPostQuestions);
    setPerClsQuestions(body.perClassificationQuestions);
    setDistribution(body.distribution);
    setInitialized(true);
  }, [target, initialized]);

  function handleAddPerPost() {
    setPerPostQuestions((prev) => [...prev, createDefaultPerPostQuestion()]);
  }

  function handleAddPerCls() {
    setPerClsQuestions((prev) => [...prev, createDefaultPerClassificationQuestion()]);
  }

  async function handleSave() {
    if (!presetId || !target) return;
    if (!displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    // AW4 #8 — 専用フォームの value をそのまま template body にマージ (未指定キーは含めない)
    const parsedDefaults: Record<string, unknown> = { ...distribution };
    setSubmitting(true);
    try {
      const templateBody: Record<string, unknown> = {
        displayName: templateDisplayName.trim(),
        description,
        targetMode: asTargetMode(targetMode),
        perPostQuestions,
        perClassificationQuestions: perClsQuestions,
        allowFreeComment,
        status,
        ...parsedDefaults,
      };
      await upsertMaster<VerificationTemplatePreset>({
        collectionName: 'verificationTemplatePresets',
        id: presetId,
        uid: 'admin-stub',
        isNew: false,
        data: {
          displayName: displayName.trim(),
          enabled: target.enabled,
          mode,
          template: templateBody,
        } as Omit<
          VerificationTemplatePreset,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
      toast('success', 'preset を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to="/masters/verification-template"
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 検証テンプレ一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">検証テンプレ — visual editor</h1>
        <p className="text-sm text-slate-500 mt-1">
          A-S24/S25 — preset の visual 編集。地区検証テンプレは preset を copy / link で取込可能。
        </p>
      </header>

      {presetsState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {presetsState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {presetsState.error.message}</p>
        </Card>
      )}

      {presetsState.status === 'ready' && !target && (
        <Card padding="md">
          <p className="text-sm text-slate-600">preset が見つかりません (id: {presetId})。</p>
        </Card>
      )}

      {target && (
        <>
          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">基本情報</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  表示名称 (displayName、master 一覧用) *
                </span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  テンプレート表示名 (template.displayName、取込先 地区テンプレの
                  displayName、空なら master の displayName を使用)
                </span>
                <input
                  type="text"
                  value={templateDisplayName}
                  onChange={(e) => setTemplateDisplayName(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">説明 (template.description)</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  disabled={submitting}
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">モード (mode、master 外側)</span>
                  {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
                  <ModeSelectField
                    value={mode}
                    onChange={(m) => setMode(m)}
                    disabled={submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  />
                </label>
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">
                    対象モード (targetMode、template.targetMode)
                  </span>
                  <ModeSelectField
                    value={targetMode}
                    onChange={(m) => setTargetMode(m)}
                    disabled={submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                    emptyOptionLabel="全モード共通 (null)"
                  />
                </label>
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">状態 (status)</span>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TemplateStatus)}
                    disabled={submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="draft">{STATUS_LABEL.draft}</option>
                    <option value="published">{STATUS_LABEL.published}</option>
                    <option value="archived">{STATUS_LABEL.archived}</option>
                  </select>
                </label>
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={allowFreeComment}
                  onChange={(e) => setAllowFreeComment(e.target.checked)}
                  disabled={submitting}
                  className="mt-1"
                />
                <span>自由記述コメント (1 投稿 1 件、最大 500 字想定) を許可</span>
              </label>
            </div>
          </Card>

          <Card padding="lg" className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">
                投稿全体への質問 ({perPostQuestions.length} 問)
              </h2>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleAddPerPost}
                disabled={submitting}
              >
                ＋ 投稿質問を追加
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              投稿 1 件に対して 1 回だけ問う質問 (description / professional_insight / score
              等の妥当性確認)。
            </p>
            <QuestionListEditor
              scope="perPost"
              value={perPostQuestions}
              onChange={setPerPostQuestions}
              disabled={submitting}
            />
          </Card>

          <Card padding="lg" className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">
                分類行ごとの質問 ({perClsQuestions.length} 問)
              </h2>
              <Button size="sm" variant="secondary" onClick={handleAddPerCls} disabled={submitting}>
                ＋ 分類質問を追加
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              bousai / walker mode の classifications 配列に対する質問。 appliesToRanks で対象 rank
              を絞り込み。
            </p>
            <QuestionListEditor
              scope="perClassification"
              value={perClsQuestions}
              onChange={setPerClsQuestions}
              disabled={submitting}
            />
          </Card>

          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">配分デフォルト (任意)</h2>
            <p className="text-xs text-slate-500 mb-3">
              AssignmentSet 作成時の推奨値。地区が preset
              を取込んでテンプレ化した際に自動入力されます。
            </p>
            {/* AW4 (docs/108 High #8) — 生 JSON textarea を専用フォームに置換 */}
            <DefaultDistributionSection
              value={distribution}
              onChange={setDistribution}
              disabled={submitting}
            />
          </Card>

          <div className="flex justify-end gap-2 mb-6">
            <Link
              to="/masters/verification-template"
              className="min-h-touch flex items-center px-4 text-sm text-slate-600 hover:text-slate-800"
            >
              キャンセル
            </Link>
            <Button onClick={() => void handleSave()} disabled={submitting}>
              {submitting ? '保存中…' : '💾 保存'}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
